import { type NotificaResponseDTO } from '@framework/api-client/api/backoffice/schemas/notificaResponseDTO.ts';
import { type NotificaUtenteResponseDTO } from '@framework/api-client/api/inquiries-compile/schemas/notificaUtenteResponseDTO.ts';
import { ISODate } from '@framework/ui/components/date/ISODate.tsx';
import { type ColumnType } from 'antd/es/table';
import { t } from 'i18next';
import { NotificationIconType } from '../detail/NotificationIconType.tsx';
import { NotificationPriorityTag } from '../detail/NotificationPriorityTag.tsx';
import { NotificationReadStatus } from './NotificationReadStatus.tsx';

export type NotificationRecord =
  | NotificaResponseDTO
  | NotificaUtenteResponseDTO;

/**
 * Identificatori di ordinamento per le colonne notifiche, condivisi tra BO e FO.
 *
 * Nota: questo enum è intenzionalmente locale per evitare di legare il modulo
 * "common" a uno specifico namespace API (es. inquiries-compile o backoffice).
 * La logica di mapping verso gli enum specifici delle API va gestita nei consumer.
 */
export enum NotificationSortField {
  OGGETTO = 'OGGETTO',
  PRIORITA = 'PRIORITA',
  DATA_NOTIFICA = 'DATA_NOTIFICA',
  DATA_PRESA_VISIONE = 'DATA_PRESA_VISIONE',
}

/**
 * Colonne base della tabella notifiche, condivise tra backoffice e frontoffice.
 * Ogni consumer seleziona e riordina le colonne necessarie, aggiungendo quelle specifiche.
 */
export const notificationColumnsBase: Record<
  string,
  ColumnType<NotificationRecord>
> = {
  letta: {
    title: t('notifiche-titolo-colonna.letta', 'Letta'),
    dataIndex: 'flVista',
    key: 'flVista',
    render: (_, record) => <NotificationReadStatus notification={record} />,
  },
  oggetto: {
    title: t('notifiche-titolo-colonna.oggetto', 'Oggetto'),
    dataIndex: 'oggetto',
    key: 'oggetto',
    className: 'col-oggetto',
    width: '18%',
    sorter: true,
    sortDataIndex: NotificationSortField.OGGETTO,
    render: (_, record) => (
      <>
        <NotificationIconType type={record.tipo as any} />
        {record.oggetto}
      </>
    ),
  },
  messaggio: {
    title: t('notifiche-titolo-colonna.messaggio', 'Messaggio'),
    dataIndex: 'messaggio',
    key: 'messaggio',
    className: 'col-messaggio',
  },
  priorita: {
    title: t('notifiche-titolo-colonna.priorita', 'Priorità'),
    dataIndex: 'priorita',
    key: 'priorita',
    sorter: true,
    sortDataIndex: NotificationSortField.PRIORITA,
    render: (text, record) => <NotificationPriorityTag notification={record} />,
  },
  dataNotifica: {
    title: t('notifiche-titolo-colonna.data-notifica', 'Data Notifica'),
    dataIndex: 'dataNotifica',
    key: 'dataNotifica',
    defaultSortOrder: 'descend',
    sorter: true,
    sortDataIndex: NotificationSortField.DATA_NOTIFICA,
    render: dataNotifica => (
      <ISODate date={dataNotifica} format="DD/MM/YYYY HH:mm:ss" />
    ),
  },
  dataLettura: {
    title: t('notifiche-titolo-colonna.data-lettura', 'Data Lettura'),
    dataIndex: 'dataPresaVisione',
    key: 'dataPresaVisione',
    sorter: true,
    sortDataIndex: NotificationSortField.DATA_PRESA_VISIONE,
    render: dataPresaVisione => (
      <ISODate date={dataPresaVisione} format="DD/MM/YYYY HH:mm:ss" />
    ),
  },
};
